/**
 * MongoDB 用户管理 API 封装
 * 所有接口使用 /api/v2 前缀，与现有系统完全独立
 */

import axios from 'axios';

const API_BASE = '/api/v2';

// 创建axios实例
const apiClient = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 请求拦截器（可添加认证token）
apiClient.interceptors.request.use(
  (config) => {
    return config;
  },
  (error) => Promise.reject(error)
);

// 响应拦截器
apiClient.interceptors.response.use(
  (response) => response.data,
  (error) => {
    console.error('MongoDB API Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

// ==================== 用户管理 ====================

export const userApi = {
  // 用户登录
  login: (data) => apiClient.post('/users/login', data),
  
  // 用户注册
  register: (data) => apiClient.post('/users/register', data),
  
  // 获取用户列表
  getList: (params) => apiClient.get('/users', { params }),

  // 获取单个用户
  getById: (userId) => apiClient.get(`/users/${userId}`),

  // 更新用户资料
  updateProfile: (userId, profile) => apiClient.put(`/users/${userId}/profile`, profile),

  // 上传用户头像
  uploadAvatar: (userId, formData) => apiClient.post(`/users/${userId}/avatar`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),

  // 更新用户状态
  updateStatus: (userId, statusData) => apiClient.put(`/users/${userId}/status`, statusData),

  // 增加积分
  addPoints: (userId, data) => apiClient.post(`/users/${userId}/points/add`, data),

  // 消耗积分
  spendPoints: (userId, data) => apiClient.post(`/users/${userId}/points/spend`, data),

  // 获取积分流水
  getPointTransactions: (userId, params) => apiClient.get(`/users/${userId}/points/transactions`, { params }),

  // 开通会员
  grantMembership: (userId, data) => apiClient.post(`/users/${userId}/membership/grant`, data),
};

// ==================== 会员套餐管理 ====================

export const membershipPlanApi = {
  // 获取所有可用套餐
  getActiveList: () => apiClient.get('/membership/plans'),

  // 创建套餐
  create: (planData) => apiClient.post('/membership/plans', planData),

  // 更新套餐
  update: (planCode, planData) => apiClient.put(`/membership/plans/${planCode}`, planData),

  // 删除（禁用）套餐
  delete: (planCode) => apiClient.delete(`/membership/plans/${planCode}`),

  // 初始化默认套餐
  initDefault: () => apiClient.post('/membership/plans/init'),
};

// ==================== 充值订单管理 ====================

export const rechargeOrderApi = {
  // 创建订单
  create: (data) => apiClient.post('/recharge/orders', data),

  // 获取订单详情
  getByNo: (orderNo) => apiClient.get(`/recharge/orders/${orderNo}`),

  // 获取用户订单列表
  getUserOrders: (userId, params) => apiClient.get(`/users/${userId}/recharge/orders`, { params }),

  // 获取所有订单（管理后台）
  getAllOrders: (params) => apiClient.get('/recharge/orders', { params }),

  // 模拟支付成功
  simulatePaymentSuccess: (orderNo, data) => apiClient.post(`/recharge/orders/${orderNo}/payment-success`, data || {}),

  // 取消订单
  cancel: (orderNo, reason) => apiClient.post(`/recharge/orders/${orderNo}/cancel`, { reason }),
};

// ==================== 统计 ====================

export const statsApi = {
  getSummary: () => apiClient.get('/stats/summary'),
};

// 健康检查
export const checkMongoDBHealth = () => apiClient.get('/health');

// ==================== 会员等级配置 ====================

/**
 * 会员等级配置
 */
export const MEMBERSHIP_LEVELS = {
  free: {
    name: '免费用户',
    nameCN: '免费用户',
    icon: '⭐',
    color: '#9ca3af',
    bgColor: 'rgba(156, 163, 175, 0.1)',
    privileges: {
      max_watch_dramas: 5,
      max_watch_quality: '720p',
      ad_free: false,
      download_enabled: false,
      early_access_days: 0,
      vip_service: false,
      discount_rate: 100
    }
  },
  bronze: {
    name: '青铜会员',
    nameCN: '青铜会员',
    icon: '🥉',
    color: '#cd7f32',
    bgColor: 'rgba(205, 127, 50, 0.1)',
    privileges: {
      max_watch_dramas: 10,
      max_watch_quality: '720p',
      ad_free: true,
      download_enabled: false,
      early_access_days: 0,
      vip_service: false,
      discount_rate: 95
    }
  },
  silver: {
    name: '白银会员',
    nameCN: '白银会员',
    icon: '🥈',
    color: '#c0c0c0',
    bgColor: 'rgba(192, 192, 192, 0.1)',
    privileges: {
      max_watch_dramas: 20,
      max_watch_quality: '1080p',
      ad_free: true,
      download_enabled: true,
      early_access_days: 1,
      vip_service: false,
      discount_rate: 90
    }
  },
  gold: {
    name: '黄金会员',
    nameCN: '黄金会员',
    icon: '🥇',
    color: '#ffd700',
    bgColor: 'rgba(255, 215, 0, 0.15)',
    privileges: {
      max_watch_dramas: 50,
      max_watch_quality: '1080p',
      ad_free: true,
      download_enabled: true,
      early_access_days: 3,
      vip_service: false,
      discount_rate: 85
    }
  },
  platinum: {
    name: '铂金会员',
    nameCN: '铂金会员',
    icon: '💎',
    color: '#e5e4e2',
    bgColor: 'rgba(229, 228, 226, 0.2)',
    privileges: {
      max_watch_dramas: 100,
      max_watch_quality: '4K',
      ad_free: true,
      download_enabled: true,
      early_access_days: 7,
      vip_service: true,
      discount_rate: 80
    }
  },
  diamond: {
    name: '钻石会员',
    nameCN: '钻石会员',
    icon: '💠',
    color: '#b9f2ff',
    bgColor: 'rgba(185, 242, 255, 0.2)',
    privileges: {
      max_watch_dramas: 9999,
      max_watch_quality: '4K',
      ad_free: true,
      download_enabled: true,
      early_access_days: 30,
      vip_service: true,
      discount_rate: 70
    }
  }
};

/**
 * 获取会员等级信息
 * @param {string} level - 会员等级代码
 * @returns {object} 会员等级信息
 */
export const getMembershipInfo = (level) => {
  return MEMBERSHIP_LEVELS[level] || MEMBERSHIP_LEVELS.free;
};

/**
 * 成长等级配置
 */
export const GROWTH_LEVELS = [
  { level: 1, name: '新星用户', min_value: 0, icon: '🌱' },
  { level: 2, name: '青铜用户', min_value: 100, icon: '🥉' },
  { level: 3, name: '白银用户', min_value: 500, icon: '🥈' },
  { level: 4, name: '黄金用户', min_value: 1000, icon: '🥇' },
  { level: 5, name: '铂金用户', min_value: 3000, icon: '💎' },
  { level: 6, name: '钻石用户', min_value: 8000, icon: '💠' },
  { level: 7, name: '星耀用户', min_value: 20000, icon: '⭐' },
  { level: 8, name: '王者用户', min_value: 50000, icon: '👑' },
  { level: 9, name: '传说用户', min_value: 100000, icon: '🌟' },
  { level: 10, name: '至尊用户', min_value: 500000, icon: '🏆' }
];

/**
 * 根据成长值获取当前等级
 * @param {number} growthValue - 成长值
 * @returns {object} 等级信息
 */
export const getGrowthLevel = (growthValue) => {
  for (let i = GROWTH_LEVELS.length - 1; i >= 0; i--) {
    if (growthValue >= GROWTH_LEVELS[i].min_value) {
      const nextLevel = GROWTH_LEVELS[i + 1];
      return {
        ...GROWTH_LEVELS[i],
        nextLevel: nextLevel || null,
        progress: nextLevel
          ? ((growthValue - GROWTH_LEVELS[i].min_value) / (nextLevel.min_value - GROWTH_LEVELS[i].min_value)) * 100
          : 100
      };
    }
  }
  return GROWTH_LEVELS[0];
};

/**
 * 格式化日期
 * @param {string|Date} date - 日期
 * @param {string} format - 格式
 * @returns {string} 格式化后的日期
 */
export const formatDate = (date, format = 'YYYY-MM-DD HH:mm:ss') => {
  if (!date) return '-';
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  const seconds = String(d.getSeconds()).padStart(2, '0');

  return format
    .replace('YYYY', year)
    .replace('MM', month)
    .replace('DD', day)
    .replace('HH', hours)
    .replace('mm', minutes)
    .replace('ss', seconds);
};

/**
 * 计算两个日期之间的天数
 * @param {string|Date} date1 - 日期1
 * @param {string|Date} date2 - 日期2
 * @returns {number} 天数差
 */
export const daysBetween = (date1, date2) => {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  return Math.ceil(Math.abs(d2 - d1) / (1000 * 60 * 60 * 24));
};

export default {
  user: userApi,
  membershipPlan: membershipPlanApi,
  rechargeOrder: rechargeOrderApi,
  stats: statsApi,
  checkHealth: checkMongoDBHealth,
  getMembershipInfo,
  getGrowthLevel,
  formatDate,
  daysBetween,
};
